import { createTheme, type Theme } from '@mui/material/styles';
import { brand, fontFamily, radius, spacingUnit, surfaces } from './tokens';

declare module '@mui/material/styles' {
  interface TypeBackground {
    sidebar: string;
  }
}

export type ColorMode = 'light' | 'dark';
export type Density = 'compact' | 'comfortable';

export interface PhoenixThemeOptions {
  mode?: ColorMode;
  /** «فشرده» برای صفحات عملیاتی (پذیرش و صندوق) — پیش‌فرض. */
  density?: Density;
  direction?: 'rtl' | 'ltr';
}

/** Theme ققنوس: RTL، Vazirmatn، روشن و تیره، Density فشرده. */
export function createPhoenixTheme(options: PhoenixThemeOptions = {}): Theme {
  const { mode = 'light', density = 'compact', direction = 'rtl' } = options;
  const compact = density === 'compact';
  const surface = surfaces[mode];

  return createTheme({
    direction,
    spacing: spacingUnit[density],
    shape: { borderRadius: radius.md },
    palette: {
      mode,
      primary: { main: brand.primary[mode], contrastText: mode === 'dark' ? '#042f2e' : '#ffffff' },
      secondary: { main: brand.secondary[mode], contrastText: mode === 'dark' ? '#1e1033' : '#ffffff' },
      background: { default: surface.background, paper: surface.paper, sidebar: surface.sidebar },
      divider: surface.divider,
      text: mode === 'dark' ? { primary: '#e7eef2', secondary: '#a8b8c2' } : { primary: '#0f172a', secondary: '#475569' },
    },
    typography: {
      fontFamily,
      fontSize: compact ? 13 : 14,
      button: { textTransform: 'none', fontWeight: 500 },
      h1: { fontSize: '1.5rem', fontWeight: 700 },
      h2: { fontSize: '1.25rem', fontWeight: 700 },
      h3: { fontSize: '1.125rem', fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { fontFeatureSettings: '"ss01"' },
        },
      },
      MuiButton: {
        defaultProps: { size: compact ? 'small' : 'medium', disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            fontWeight: 600,
            '&.Mui-focusVisible': { outline: `2px solid ${brand.primary[mode]}`, outlineOffset: 2 },
          },
        },
      },
      MuiIconButton: {
        defaultProps: { size: compact ? 'small' : 'medium' },
        styleOverrides: {
          root: { '&.Mui-focusVisible': { outline: `2px solid ${brand.primary[mode]}`, outlineOffset: 2 } },
        },
      },
      MuiTextField: { defaultProps: { size: compact ? 'small' : 'medium', fullWidth: true } },
      MuiFormControl: { defaultProps: { size: compact ? 'small' : 'medium', fullWidth: true } },
      MuiSelect: { defaultProps: { size: compact ? 'small' : 'medium' } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.04)' : surface.paper,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
          },
        },
      },
      MuiTable: { defaultProps: { size: compact ? 'small' : 'medium' } },
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#f8fafc',
            color: mode === 'dark' ? '#e7eef2' : '#0f172a',
            fontWeight: 600,
          },
        },
      },
      MuiList: { defaultProps: { dense: compact } },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            marginInline: 8,
            minHeight: 40,
            '&.Mui-selected': {
              backgroundColor: mode === 'dark' ? 'rgba(45, 212, 191, 0.16)' : 'rgba(15, 118, 110, 0.12)',
            },
            '&.Mui-selected:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(45, 212, 191, 0.24)' : 'rgba(15, 118, 110, 0.18)',
            },
            '&.Mui-focusVisible': { outline: `2px solid ${brand.primary[mode]}`, outlineOffset: -2 },
          },
        },
      },
      MuiMenuItem: { defaultProps: { dense: compact } },
      MuiTooltip: { defaultProps: { arrow: true } },
      MuiChip: { defaultProps: { size: 'small' } },
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'inherit' },
        styleOverrides: {
          root: { backgroundColor: surface.paper, color: mode === 'dark' ? '#e7eef2' : '#0f172a', backgroundImage: 'none' },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { backgroundColor: surface.sidebar, backgroundImage: 'none', borderInlineEnd: `1px solid ${surface.divider}` },
        },
      },
      MuiDialog: { styleOverrides: { paper: { borderRadius: radius.lg } } },
    },
  });
}
